import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    EasyFlexWorker,
    EasyflexFreshness,
    EasyflexStatusFilter,
    EASYFLEX_STATUS_FILTER_OPTIONS,
    bulkImportEasyflexWorkers,
    checkEasyflexLinksBulk,
    fetchEasyFlexWorkers,
    getEasyflexDataSyncBySyncRunId,
    startEasyflexDataSyncJob,
} from "../api/workersApi";
import { useDebounce } from "../utils/useDebounce";
import { extractErrorMessageSync } from "../utils/errorHandler";

export type EasyflexSortField =
    | "name" | "status" | "occupation" | "placement" | "business" | "experience" | "hours" | "updatedAt";
export type EasyflexSortOrder = "asc" | "desc";

export interface EasyflexSnackbar {
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
}

/** localStorage keys — kept identical to the old workers-tab so prefs survive the move. */
const LS = {
    freshness: "optima.easyflex.workersTable.freshness",
    syncKey: "optima.easyflex.workersTable.syncKey",
    organizations: "optima.easyflex.workersTable.organizations",
    lastSyncRunId: "optima.easyflex.workersTable.lastSyncRunId",
    status: "optima.easyflex.workersTable.status",
} as const;

const readLS = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};
const writeLS = (key: string, value: string | null) => {
    try {
        if (value === null || value === "") localStorage.removeItem(key);
        else localStorage.setItem(key, value);
    } catch {
        // ignore
    }
};

/**
 * All state + data-fetching for the standalone EasyFlex worker page: list fetch,
 * freshness/sync-job orchestration, granular status filter, sorting, search,
 * pagination, and bulk-import selection. Extracted verbatim from the old
 * EasyFlex tab in WorkersTable so behaviour is preserved.
 */
export function useEasyflexWorkers() {
    const navigate = useNavigate();
    const fetchSequenceRef = useRef(0);

    const [workers, setWorkers] = useState<EasyFlexWorker[]>([]);
    const [linkedMap, setLinkedMap] = useState<Map<string, { workerId: string; name: string }>>(new Map());
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const [freshness, setFreshnessState] = useState<EasyflexFreshness>(() => {
        const raw = readLS(LS.freshness);
        return raw === "cached" || raw === "refresh_async" || raw === "refresh_sync" ? raw : "cached";
    });
    const [syncKey, setSyncKeyState] = useState<string>(() => {
        const raw = readLS(LS.syncKey);
        return raw && new Set(["workers", "placements", "hours", "all"]).has(raw) ? raw : "workers";
    });
    const [organizationsInput, setOrganizationsInputState] = useState<string>(() => readLS(LS.organizations) || "");
    const [lastSyncRunId, setLastSyncRunId] = useState<string | null>(() => readLS(LS.lastSyncRunId));
    const [lastSyncState, setLastSyncState] = useState<string | null>(null);
    const [lastSyncError, setLastSyncError] = useState<string | null>(null);

    const [statusFilter, setStatusFilterState] = useState<"" | EasyflexStatusFilter>(() => {
        const raw = readLS(LS.status);
        return EASYFLEX_STATUS_FILTER_OPTIONS.some((o) => o.value === raw) ? (raw as EasyflexStatusFilter) : "";
    });
    const [sortField, setSortField] = useState<EasyflexSortField>("name");
    const [sortOrder, setSortOrder] = useState<EasyflexSortOrder>("asc");

    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounce(search, 500);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [bulkSelectMode, setBulkSelectMode] = useState(false);
    const [selected, setSelected] = useState<Map<string, EasyFlexWorker>>(() => new Map());
    const [isSelectingAllMatching, setIsSelectingAllMatching] = useState(false);
    const [allMatchingSelected, setAllMatchingSelected] = useState(false);
    const [bulkImporting, setBulkImporting] = useState(false);

    const [snackbar, setSnackbar] = useState<EasyflexSnackbar>({ open: false, message: "", severity: "info" });

    // ----- persisted setters -----
    const setFreshness = useCallback((v: EasyflexFreshness) => { setFreshnessState(v); writeLS(LS.freshness, v); }, []);
    const setSyncKey = useCallback((v: string) => { setSyncKeyState(v); writeLS(LS.syncKey, v); }, []);
    const setOrganizationsInput = useCallback((v: string) => { setOrganizationsInputState(v); writeLS(LS.organizations, v); }, []);
    const setStatusFilter = useCallback((v: "" | EasyflexStatusFilter) => {
        setStatusFilterState(v);
        writeLS(LS.status, v || null);
        setPage(0);
    }, []);

    const parseOrganizations = (raw: string): string[] | undefined => {
        const parts = String(raw || "").split(",").map((s) => s.trim()).filter(Boolean);
        return parts.length ? parts : undefined;
    };

    const fetchWorkers = useCallback(
        async (isRefresh = false) => {
            const fetchSeq = ++fetchSequenceRef.current;
            if (isRefresh) setRefreshing(true);
            else setLoading(true);

            try {
                const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
                const waitForSyncRun = async (syncRunId: string) => {
                    const timeoutMs = 2 * 60 * 1000;
                    const intervalMs = 1500;
                    const start = Date.now();
                    while (Date.now() - start < timeoutMs) {
                        if (fetchSequenceRef.current !== fetchSeq) return;
                        try {
                            const response = await getEasyflexDataSyncBySyncRunId(syncRunId);
                            const job = response?.job;
                            if (job?.state) setLastSyncState(job.state);
                            if (job?.failedReason) setLastSyncError(job.failedReason);
                            if (job?.state === "completed" || job?.state === "failed") return;
                        } catch (error) {
                            setLastSyncError(extractErrorMessageSync(error));
                        }
                        await sleep(intervalMs);
                    }
                    throw new Error("Timed out waiting for Easyflex sync to finish");
                };

                if (isRefresh && freshness !== "cached") {
                    setLastSyncState(null);
                    setLastSyncError(null);
                    const organizations = parseOrganizations(organizationsInput);
                    const { syncRunId } = await startEasyflexDataSyncJob(syncKey, { organizations });
                    if (syncRunId) {
                        setLastSyncRunId(syncRunId);
                        writeLS(LS.lastSyncRunId, syncRunId);
                    } else {
                        setSnackbar({ open: true, severity: "info", message: "Easyflex refresh job already running (no new syncRunId returned)." });
                    }
                    if (freshness === "refresh_sync" && syncRunId) {
                        try {
                            await waitForSyncRun(syncRunId);
                        } catch (error) {
                            setSnackbar({ open: true, severity: "warning", message: extractErrorMessageSync(error) });
                        }
                    }
                }

                const easyflexFilters = {
                    page,
                    limit: rowsPerPage,
                    search: debouncedSearch,
                    sortBy: sortField,
                    sortOrder,
                    ...(statusFilter && { easyflexStatus: statusFilter }),
                };
                const response = await fetchEasyFlexWorkers(easyflexFilters);
                if (fetchSequenceRef.current !== fetchSeq) return;
                setWorkers(response.data);
                setTotalCount(response.pagination.total);

                // Best-effort: flag which visible rows are already imported.
                try {
                    const regnums = response.data
                        .map((w) => w.easyflexRegistrationNumber)
                        .filter((r): r is number => r != null);
                    const linked = await checkEasyflexLinksBulk(regnums);
                    if (fetchSequenceRef.current !== fetchSeq) return;
                    const map = new Map<string, { workerId: string; name: string }>();
                    for (const l of linked) {
                        const key = `${l.easyflexRegistrationNumber}::${l.easyflexOrganization ?? ""}`;
                        map.set(key, { workerId: l.workerId, name: [l.firstName, l.lastName].filter(Boolean).join(" ") });
                    }
                    setLinkedMap(map);
                } catch {
                    setLinkedMap(new Map());
                }
            } catch (error) {
                setSnackbar({ open: true, message: extractErrorMessageSync(error), severity: "error" });
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [debouncedSearch, freshness, organizationsInput, page, rowsPerPage, sortField, sortOrder, statusFilter, syncKey],
    );

    // Cached fetch whenever a query input changes; sync jobs only fire on explicit refresh().
    useEffect(() => {
        fetchWorkers(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, page, rowsPerPage, sortField, sortOrder, statusFilter]);

    const refresh = useCallback(() => fetchWorkers(true), [fetchWorkers]);

    // ----- selection -----
    const toggleRow = useCallback((w: EasyFlexWorker) => {
        setSelected((prev) => {
            const next = new Map(prev);
            const key = String(w.id);
            if (next.has(key)) next.delete(key);
            else next.set(key, w);
            return next;
        });
        setAllMatchingSelected(false);
    }, []);

    const togglePage = useCallback((workersOnPage: EasyFlexWorker[], nextChecked: boolean) => {
        setSelected((prev) => {
            const next = new Map(prev);
            for (const w of workersOnPage) {
                const key = String(w.id);
                if (nextChecked) next.set(key, w);
                else next.delete(key);
            }
            return next;
        });
        setAllMatchingSelected(false);
    }, []);

    const selectAllMatching = useCallback(async () => {
        try {
            setIsSelectingAllMatching(true);
            const resp = await fetchEasyFlexWorkers({
                page: 0,
                limit: 2000,
                search: debouncedSearch || undefined,
                sortBy: sortField,
                sortOrder,
                ...(statusFilter && { easyflexStatus: statusFilter }),
            });
            const next = new Map<string, EasyFlexWorker>();
            for (const w of resp.data) next.set(String(w.id), w);
            setSelected(next);
            setAllMatchingSelected(true);
        } catch (err) {
            setSnackbar({ open: true, message: `Failed to select all matching: ${extractErrorMessageSync(err)}`, severity: "error" });
        } finally {
            setIsSelectingAllMatching(false);
        }
    }, [debouncedSearch, sortField, sortOrder, statusFilter]);

    const clearSelection = useCallback(() => {
        setSelected(new Map());
        setAllMatchingSelected(false);
    }, []);

    const toggleBulkSelectMode = useCallback(() => {
        setBulkSelectMode((prev) => {
            if (prev) clearSelection();
            return !prev;
        });
    }, [clearSelection]);

    const bulkImport = useCallback(async () => {
        const items: EasyFlexWorker[] = Array.from(selected.values());
        if (items.length === 0) return;
        setBulkImporting(true);
        try {
            const result = await bulkImportEasyflexWorkers(items);
            const { created, alreadyImported, errors } = result.summary;
            const severity: EasyflexSnackbar["severity"] = errors > 0 ? "warning" : created > 0 ? "success" : "info";
            setSnackbar({ open: true, message: `Imported ${created} • already in recruitment: ${alreadyImported} • failed: ${errors}`, severity });
            clearSelection();
            if (created > 0) navigate("/resources/workers", { state: { openTab: "incomplete" } });
        } catch (err) {
            setSnackbar({ open: true, message: `Bulk import failed: ${extractErrorMessageSync(err)}`, severity: "error" });
        } finally {
            setBulkImporting(false);
        }
    }, [clearSelection, navigate, selected]);

    const selectedIds = useMemo(() => new Set(Array.from(selected.keys())), [selected]);

    return {
        // data
        workers, linkedMap, totalCount, loading, refreshing,
        // sync/freshness
        freshness, setFreshness, syncKey, setSyncKey, organizationsInput, setOrganizationsInput,
        lastSyncRunId, lastSyncState, lastSyncError,
        // filter/sort/search/pagination
        statusFilter, setStatusFilter,
        sortField, sortOrder,
        setSort: (field: EasyflexSortField, dir: EasyflexSortOrder) => { setSortField(field); setSortOrder(dir); setPage(0); },
        search, setSearch: (v: string) => { setSearch(v); setPage(0); },
        page, setPage, rowsPerPage, setRowsPerPage,
        refresh,
        // selection / bulk import
        bulkSelectMode, toggleBulkSelectMode,
        selected, selectedIds, selectedCount: selected.size,
        toggleRow, togglePage, selectAllMatching, isSelectingAllMatching, allMatchingSelected,
        clearSelection, bulkImport, bulkImporting,
        // navigation helpers for row actions
        openImportWizard: (w: EasyFlexWorker) =>
            navigate(`/resources/workers/import/${encodeURIComponent(String(w.id))}`, { state: { worker: w } }),
        openWorker: (workerId: string) => navigate(`/workers/${encodeURIComponent(workerId)}`),
        // snackbar
        snackbar, dismissSnackbar: () => setSnackbar((s) => ({ ...s, open: false })),
    };
}
